import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import api from '../../../services/api';
import { formatIsoDateOnly } from '../../../utils/dateUtils';
import type { UnbilledChargeRow } from './types';

type ApprovalEvent = {
  old_approval: string;
  new_approval: string;
  changed_by: string;
  changed_at: string | null;
  source: string;
};

export default function ApprovalHistoryHover({ row, note, children }: {
  row: UnbilledChargeRow;
  note?: string;
  children: ReactNode;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<ApprovalEvent[] | null>(null);
  const [error, setError] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || events !== null || error) return;
    let active = true;
    api.get<ApprovalEvent[]>('/unbilled-charges', {
      params: { approval_history_pro: row.pronumber, approval_history_category: row.category },
    }).then(({ data }) => {
      if (active) setEvents(data);
    }).catch(() => {
      if (active) setError(true);
    });
    return () => { active = false; };
  }, [open, events, error, row.pronumber, row.category]);

  useEffect(() => () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }, []);

  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const closeSoon = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  const show = () => {
    cancelClose();
    const rect = anchor.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 210)),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 330)),
      });
    }
    setOpen(true);
  };

  return <>
    <div ref={anchor} onMouseEnter={show} onMouseLeave={closeSoon}
      onFocusCapture={show} onBlurCapture={closeSoon}>{children}</div>
    {open && createPortal(
      <div role="tooltip" onMouseEnter={cancelClose} onMouseLeave={closeSoon}
        className="fixed z-[120] w-80 max-h-52 overflow-y-auto rounded-md border border-slate-300 bg-panel p-3 text-xs text-body shadow-xl dark:border-gray-600"
        style={{ top: position.top, left: position.left }}>
        {note && <p className="mb-2">{note}</p>}
        <p className="font-semibold">Approval history</p>
        {error ? <p>Could not load history.</p>
          : events === null ? <p>Loading…</p>
          : events.length === 0 ? <p>No approval changes recorded.</p>
          : <ul className="mt-1 space-y-1.5">{events.map((event, index) => (
              <li key={`${event.changed_at}-${index}`} className="border-t border-slate-200 pt-1 dark:border-gray-600">
                {event.changed_at ? `${formatIsoDateOnly(event.changed_at)} ${event.changed_at.slice(11, 16)}` : '—'} · {event.changed_by || '—'} · {event.old_approval || '—'} → {event.new_approval || '—'}
                {event.source !== 'user' && <span className="ml-1 text-muted">({event.source === 'legacy-backfill' ? 'legacy backfill' : event.source === 'legacy-cleanup' ? 'legacy correction' : event.source})</span>}
              </li>
            ))}</ul>}
      </div>, document.body,
    )}
  </>;
}
